import type { FormularSeite, Katalog, Knopf } from "@gui-creator/gemeinsam";
import { ImageIcon, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ItemBild } from "../inventar/ItemBild.tsx";
import { useSpielschrift } from "../inventar/schrift.ts";
import { SpielText } from "../inventar/SpielText.tsx";
import "./bedrock.css";

const TEXT = 1.5;
// Nutzbare Breiten in Spielpixeln bei 320 px Formularbreite.
const TEXT_BREITE = 192;
const KNOPF_BREITE = 132;

export function BedrockFormular({ seiten, katalog }: { seiten: FormularSeite[]; katalog: Katalog }) {
  const glyphen = useSpielschrift();
  const [seite, setSeite] = useState(0);
  const [frage, setFrage] = useState<string | null>(null);
  const itemZuBild = useMemo(() => new Map(Object.entries(katalog.bedrockBilder).map(([item, bild]) => [bild, item])), [katalog.bedrockBilder]);
  const rollen = katalog.farbrollen;

  useEffect(() => {
    if (seite >= seiten.length) setSeite(Math.max(0, seiten.length - 1));
  }, [seite, seiten.length]);

  const s = seiten[Math.min(seite, seiten.length - 1)];
  if (!s) return null;

  const druecken = (k: Knopf) => {
    if (k.navigation === "zurueck") setSeite((x) => Math.max(0, x - 1));
    else if (k.navigation === "weiter") setSeite((x) => Math.min(seiten.length - 1, x + 1));
    else if (k.bestaetigung) setFrage(k.bestaetigung);
  };

  const bild = (k: Knopf) => {
    if (!k.bild) return null;
    const item = itemZuBild.get(k.bild);
    return (
      <span className="bedrock-bild" title={k.bild}>
        {item ? <ItemBild symbol={item} groesse={32} /> : <ImageIcon size={20} aria-hidden="true" />}
      </span>
    );
  };

  return (
    <div className="bedrock" role="region" aria-label="Bedrock-Formular">
      <div className="bedrock-kopf">
        <SpielText text={frage ? "Bestätigung" : s.titel} rollen={rollen} glyphen={glyphen} skala={TEXT * 1.15} farbe="#FFFFFF" bedrock />
        <span className="bedrock-zu" aria-hidden="true">
          <X size={16} />
        </span>
      </div>
      {frage ? (
        <div className="bedrock-koerper">
          <div className="bedrock-text">
            <SpielText text={frage} rollen={rollen} glyphen={glyphen} skala={TEXT} farbe="#FFFFFF" bedrock />
          </div>
          <div className="bedrock-modal-knoepfe">
            <button type="button" className="bedrock-knopf" onClick={() => setFrage(null)}>
              <SpielText text="Ja" rollen={rollen} glyphen={glyphen} skala={TEXT} farbe="#1E1E1F" bedrock />
            </button>
            <button type="button" className="bedrock-knopf" onClick={() => setFrage(null)}>
              <SpielText text="Nein" rollen={rollen} glyphen={glyphen} skala={TEXT} farbe="#1E1E1F" bedrock />
            </button>
          </div>
        </div>
      ) : (
        <div className="bedrock-koerper">
          {s.inhalt.map((block, i) => (
            <div key={i} className="bedrock-text">
              <SpielText text={block} rollen={rollen} glyphen={glyphen} skala={TEXT} farbe="#FFFFFF" bedrock umbruch={TEXT_BREITE} />
            </div>
          ))}
          {s.knoepfe.length === 0 && <p className="bedrock-leer">Keine Knöpfe.</p>}
          {s.knoepfe.map((k, i) => (
            <button key={i} type="button" className="bedrock-knopf" onClick={() => druecken(k)}>
              {bild(k)}
              <span className="bedrock-knopf-text">
                <SpielText text={k.zeile === undefined ? k.name : `${k.name}\n${k.zeile}`} rollen={rollen} glyphen={glyphen} skala={TEXT} farbe="#1E1E1F" bedrock zentriert umbruch={KNOPF_BREITE} />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
